"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { Typography } from "@repo/ui/aops-components/typography";
import { Badge, Group } from "@repo/ui/mantine";
import type { CoursePageSection } from "@repo/sanity-utils/query-types";
import { getKeyedCopyText } from "@repo/utils/keyedCopy";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import styles from "../CourseLevelPage.module.css";
import topicBadgeStyles from "./TopicsCovered.module.css";
export type TopicsCoveredProps = {
  topics?: string[];
  courseSlug: string;
  coursePageSection?: NonNullable<CoursePageSection>;
};
const TOPICS_LABEL_KEY = "topics-label";
const FALLBACK_TOPICS_LABEL = "Core topics";

/**
 * `course_syllabus_viewed` is mapped to this "Core topics" section for now —
 * there's no dedicated syllabus UI in aops. Provisional pending a
 * marketing/PM decision on what should actually represent "syllabus viewed".
 */
export function TopicsCovered(t0) {
  const $ = _c(14);
  const {
    topics,
    courseSlug,
    coursePageSection
  } = t0;
  const sectionRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== courseSlug) {
    t1 = () => {
      const section = sectionRef.current;
      if (!section) {
        return;
      }
      let hasScrolled = false;
      let isIntersecting = false;
      const tryFire = () => {
        if (isIntersecting && hasScrolled) {
          sendDiscoveryEventBeacon("course_syllabus_viewed", {
            course_slug: courseSlug
          });
          cleanup();
        }
      };
      const onScroll = () => {
        hasScrolled = true;
        tryFire();
      };
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      const observer = new IntersectionObserver(t3 => {
        const [entry] = t3;
        isIntersecting = entry?.isIntersecting ?? false;
        tryFire();
      });
      observer.observe(section);
      function cleanup() {
        observer.disconnect();
        window.removeEventListener("scroll", onScroll);
      }
      return cleanup;
    };
    t2 = [courseSlug];
    $[0] = courseSlug;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!topics?.length) {
    return null;
  }
  let t3;
  if ($[3] !== coursePageSection?.keyedCopy) {
    t3 = getKeyedCopyText(coursePageSection?.keyedCopy ?? [], TOPICS_LABEL_KEY) || FALLBACK_TOPICS_LABEL;
    $[3] = coursePageSection?.keyedCopy;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const topicsLabel = t3;
  let t4;
  if ($[5] !== topicsLabel) {
    t4 = <Typography as="h3" variant="body" className={styles.labeledParagraphTitle}>{topicsLabel}</Typography>;
    $[5] = topicsLabel;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== topics) {
    t5 = topics.map(_temp);
    $[7] = topics;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <Group className={styles.labeledParagraphContent} gap="var(--dimension-padding-3xs)" wrap="wrap" role="list">{t5}</Group>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <div className={styles.labeledParagraphs} ref={sectionRef}><div className={styles.labeledParagraph}>{t4}{t6}</div></div>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function _temp(topic) {
  return <Badge key={topic} classNames={{
    root: topicBadgeStyles.badge,
    label: topicBadgeStyles.badgeLabel
  }} variant="outline" size="lg" radius="none" role="listitem">{topic}</Badge>;
}